<script lang="ts">
    import { supabase } from '$lib/supabase';
    import { fade, fly } from 'svelte/transition';
    import { createEventDispatcher } from 'svelte';
    import { LogIn, UserPlus, X } from 'lucide-svelte';
    import { browser } from '$app/environment';
    import { setAuthState, refreshSession } from '$lib/stores/authStore';
    const dispatch = createEventDispatcher();

    export let message = 'アカウントにログインしてください';
    let email = '';
    let password = '';
    let isLogin = true;
    let error = '';

    async function handleGoogleSignIn() {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
        });
        if (error) {
            alert('Googleログインに失敗しました。');
            console.error('Google login error:', error);
        } else {
            if (browser) {
                refreshSession();
            }
        }
    }

    async function handleSubmit() {
        error = '';
        if (isLogin) {
            const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
            if (signInError) error = signInError.message;
            else {
                if (browser) {
                    refreshSession();
                    dispatch('close');
                }
            }
        } else {
            const { error: signUpError } = await supabase.auth.signUp({ email, password });
            if (signUpError) error = signUpError.message;
            else {
                alert('確認メールを送信しました。メールを確認してアカウントを有効化してください。');
                dispatch('close');
            }
        }
    }

    function toggleMode() {
        isLogin = !isLogin;
        error = '';
    }
</script>

<div class="fixed inset-0 z-50 flex items-center justify-center px-4" transition:fade>
    <div class="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-sm" on:click={() => dispatch('close')}></div>
    
    <div class="relative bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md p-8 border border-black/[0.05] dark:border-white/[0.05]"
        transition:fly={{ y: 20, duration: 300 }}>
        
        <button
            class="absolute top-4 right-4 text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
            on:click={() => dispatch('close')}
        >
            <X class="w-5 h-5" />
        </button>

        <div class="text-center mb-8">
            <div class="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-zinc-100 dark:bg-zinc-800">
                {#if isLogin}
                    <LogIn class="w-6 h-6 text-black/60 dark:text-white/60" />
                {:else}
                    <UserPlus class="w-6 h-6 text-black/60 dark:text-white/60" />
                {/if}
            </div>
            <h2 class="text-xl font-light tracking-wide text-black/90 dark:text-white/90">
                {isLogin ? 'おかえりなさい' : '新規登録'}
            </h2>
            <p class="text-sm text-black/50 dark:text-white/50 mt-2">
                {message}
            </p>
        </div>

        <form on:submit|preventDefault={handleSubmit} class="space-y-4">
            <button 
                type="button"
                on:click={handleGoogleSignIn}
                class="w-full py-3 bg-white text-black border border-gray-300 rounded-lg text-sm font-medium tracking-wider 
                transition-all duration-200 hover:bg-gray-100 flex items-center justify-center"
            >
                <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#FFC107"/>
                    <path d="M3.15302 7.3455L6.43851 9.755C7.32752 7.554 9.48052 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15902 2 4.82802 4.1685 3.15302 7.3455Z" fill="#FF3D00"/>
                    <path d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5717 17.5742 13.3037 18.001 12 18C9.39897 18 7.19047 16.3415 6.35847 14.027L3.09747 16.5395C4.75247 19.778 8.11347 22 12 22Z" fill="#4CAF50"/>
                    <path d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.7845L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#1976D2"/>
                </svg>

                {isLogin ? 'Googleでログイン' : 'Googleで登録'}
            </button>

            <div class="relative flex items-center justify-center">
                <div class="flex-grow border-t border-gray-300"></div>
                <span class="mx-4 text-sm text-gray-500">または</span>
                <div class="flex-grow border-t border-gray-300"></div>
            </div>

            <div>
                <input
                    type="email"
                    placeholder="メールアドレス"
                    bind:value={email}
                    class="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg text-sm 
                    placeholder:text-black/30 dark:placeholder:text-white/30 
                    border border-black/[0.05] dark:border-white/[0.05] 
                    focus:outline-none focus:ring-1 focus:ring-black/10 dark:focus:ring-white/10"
                />
            </div>
            <div>
                <input
                    type="password"
                    placeholder="パスワード"
                    bind:value={password}
                    class="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg text-sm 
                    placeholder:text-black/30 dark:placeholder:text-white/30 
                    border border-black/[0.05] dark:border-white/[0.05] 
                    focus:outline-none focus:ring-1 focus:ring-black/10 dark:focus:ring-white/10"
                />
            </div>

            {#if error}
                <p class="text-sm text-red-500/90 dark:text-red-400/90 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-md">{error}</p>
            {/if}

            <button
                type="submit"
                class="w-full py-3 bg-zinc-900 dark:bg-white text-white dark:text-black rounded-lg text-sm tracking-wider 
                transition-all duration-200 hover:opacity-90 flex items-center justify-center"
            >
                {#if isLogin}
                    <LogIn class="w-4 h-4 mr-2 opacity-70" />
                    ログイン
                {:else}
                    <UserPlus class="w-4 h-4 mr-2 opacity-70" />
                    アカウントを作成
                {/if}
            </button>
        </form>

        <button
            type="button"
            class="w-full mt-6 text-sm text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white transition-colors"
            on:click={toggleMode}
        >
            {isLogin ? 'アカウントをお持ちでない方' : 'すでにアカウントをお持ちの方'}は
            <span class="font-medium text-black/70 dark:text-white/70">
                {isLogin ? '新規登録' : 'ログイン'}
            </span>
        </button>
    </div>
</div>